import { PlatformIcon } from '../profile/links';

export interface Simulcast {
  platform: string;
  label: string;
  url: string;
}

/**
 * "Watch on Twitch" — where else a broadcast is going out (§6.8).
 *
 * ## One component, because it appears in four places
 *
 * The profile's live section, the on-air modal, the dashboard rail, and the
 * console's own preview of what listeners see. Four copies of an outbound link
 * would eventually be four different opinions about `rel`, and the one that
 * forgot `noopener` is the one that ships.
 *
 * ## It never renders without a destination
 *
 * The server resolves the URL from the host's own profile link and returns null
 * when that link is gone, so `simulcast` being present is the same statement as
 * "this goes somewhere". Nothing here has to guess.
 *
 * ## Sometimes it must not be a link at all
 *
 * The dashboard rail and the on-air modal draw whole rows that are themselves
 * links, and an anchor inside an anchor is invalid HTML that browsers silently
 * un-nest - producing a stray link outside the row and a dead area inside it.
 * `interactive: false` renders the same thing as plain text for those, and the
 * row's own destination carries the click.
 */
export function SimulcastLink({
  simulcast,
  size = 'md',
  interactive = true,
}: {
  simulcast: Simulcast | null;
  /** `sm` for list rows, where it is one fact among several. */
  size?: 'sm' | 'md';
  /** False inside something that is already a link. See above. */
  interactive?: boolean;
}) {
  if (!simulcast) return null;

  const small = size === 'sm';

  if (!interactive) {
    return (
      <span
        data-simulcast-tag={simulcast.platform}
        className="inline-flex shrink-0 items-center gap-1 font-mono text-[10px] text-hot-text"
      >
        <PlatformIcon platform={simulcast.platform} className="h-3 w-3" />
        {simulcast.label}
      </span>
    );
  }

  return (
    <a
      href={simulcast.url}
      target="_blank"
      /*
       * `noopener` denies the opened page a handle on this one. `nofollow`
       * because a host chose this destination, not us. The server pinned the
       * URL to the platform's own hosts, so this cannot point anywhere the
       * label does not claim.
       */
      rel="noopener noreferrer nofollow"
      data-simulcast-link={simulcast.platform}
      title={`Watch this set live on ${simulcast.label}`}
      className={
        small
          ? 'inline-flex items-center gap-1 font-mono text-[10px] text-hot-text hover:underline'
          : 'inline-flex items-center gap-2 rounded-[var(--radius-pill)] border border-hot-edge bg-hot-wash px-3.5 py-2 text-[13px] font-medium text-hot-text transition-opacity hover:opacity-85'
      }
      onClick={(event) => event.stopPropagation()}
    >
      <PlatformIcon platform={simulcast.platform} className={small ? 'h-3 w-3' : 'h-4 w-4'} />
      {small ? simulcast.label : `Watch on ${simulcast.label}`}
    </a>
  );
}
